import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { Card, Table, TableHead, TableBody, TableRow, TableCell, Typography, IconButton, CircularProgress, Grid } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SearchableSelect from "components/SearchableSelect";
import CountryCodes from "components/CountryCodes";
import SoftInput from "components/SoftInput";
import SoftSnackbar from "components/SoftSnackbar";
import SoftPagination from "components/SoftPagination";
import Icon from "@mui/material/Icon";
import SoftTypography from "components/SoftTypography";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";
import FlagLoader from "layouts/flagloader";

const MorningConsultSessionsList = () => {
    /* ---------- PERMISSION SETUP ---------- */
    const { permissions: ctxPerms = [], isAuthReady } = useAuth();
    const storedPerms = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("permissions") || "[]");
        } catch {
            return (localStorage.getItem("permissions") || "").split(",").filter(Boolean);
        }
    }, []);

    const permSet = useMemo(() => {
        const permissions = ctxPerms.length ? ctxPerms : storedPerms;
        return new Set(
            permissions.map(p => typeof p === "string" ? p : p.name)
        );
    }, [ctxPerms, storedPerms]);

    // Permission check functions
    const userHas = useCallback(
        (colKey) => permSet.has(`viewstudies.${colKey}`),
        [permSet]
    );

    const userCanSearch = useCallback(
        (fieldKey) => permSet.has(`searchstudies.${fieldKey}`),
        [permSet]
    );

    const userCanExport = useCallback(
        () => permSet.has("exportstudies"),
        [permSet]
    );

    /* ---------- STATE VARIABLES ---------- */
    const { id } = useParams();
    const [sessions, setSessions] = useState([]);
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [shifts, setShifts] = useState([]);
    const [selectedShift, setSelectedShift] = useState(null);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [totalCompletes, setTotalCompletes] = useState(0);
    const [status, setStatus] = useState([]);
    const [filterSurvey, setFilterSurvey] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [message, setMessage] = useState(null);
    const [surveys, setSurveys] = useState([]);
    const [show, setShow] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedCountryCode, setSelectedCountryCode] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [totalSessions, setTotalSessions] = useState(0);
    const [fromData, setFromData] = useState(0);
    const [toData, setToData] = useState(0);
    const [client_survey_id, setClient_survey_id] = useState([]);
    const [client_project_id, setClient_project_id] = useState([]);
    const [survey_name, setSurvey_name] = useState([]);
    const [survey_names, setSurvey_names] = useState([]);
    const [qsidArray, setQsidArray] = useState([]);
    const [qsid, setQsid] = useState([]);
    const [qsidNameChange, setQsidNameChange] = useState([]);
    const [clients, setClients] = useState([]);
    const [clientSubAccounts, setClientSubAccounts] = useState([]);
    const [selectedClientSubAccounts, setSelectedClientSubAccounts] = useState([]);
    const [selectedClient, setSelectedClient] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const token = localStorage.getItem("authToken");

    /* ---------- COLUMN DEFINITIONS WITH PERMISSIONS ---------- */
    // Updated column definitions as per request:
    // - Client Survey ID -> bid_id
    // - Client Project ID renamed to Client quota id -> quota_id
    // - Survey CPI -> cpi
    // - Supplier ID renamed to Status Detail -> status_detail
    // - New column: Qualified At -> qualified_at
    // - Start Date -> created_at
    // - End Date -> finished_at
    // - Removed: location, ip_address, browser, device_os, user_agent
    const columnDefs = [
        { key: "client_survey_id", label: "Client Survey ID" },
        { key: "client_quota_id", label: "Client quota id" },
        // { key: "survey_name", label: "Survey Name" },
        // { key: "user", label: "User" },
        // { key: "client", label: "Client" },
        { key: "respondent_id", label: "Respondent ID" },
        { key: "survey_cpi", label: "Survey CPI" },
        { key: "status", label: "Status" },
        { key: "status_detail", label: "Status Detail" },
        { key: "qualified_at", label: "Qualified At" },
        { key: "start_date", label: "Start Date" },
        { key: "end_date", label: "End Date" }
    ];

    // const visibleColumns = useMemo(
    //     () => columnDefs.filter((c) => userHas(c.key)),
    //     [columnDefs, userHas]
    // );
    const visibleColumns = columnDefs;
    /* ---------- FILTER OPTIONS ---------- */
    // const statuses = [
    //     { value: "0", label: "Initiate" },
    //     { value: "1", label: "Completed" },
    //     { value: "2", label: "Terminate" },
    //     { value: "3", label: "Quota Full" },
    //     { value: "4", label: "S.Terminate" },
    //     { value: "5", label: "Q.Terminate/Blacklist" },
    //     { value: "6", label: "Survey not Available" },
    //     { value: "7", label: "Survey Timeout" }
    // ];

    const statuses = [
        { value: "complete", label: "Complete" },
        { value: "failure", label: "Failure" },
        { value: "over_quota", label: "Over Quota" },
        { value: "quality_termination", label: "Quality Termination" },
        { value: "screenout", label: "Screenout" },
        { value: "timeout", label: "Timeout" },
        { value: "in_progress", label: "In Progress" }
    ];

    const countryCodes = CountryCodes().map((code) => ({
        value: code,
        label: code,
    }));

    /* ---------- HANDLER FUNCTIONS ---------- */
    const handleInputChange = (event) => {
        const value = event.target.value;
        if (value === '') {
            setCurrentPage('');
            return;
        }
        const pageNumber = Number(value);
        if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(prevPage => prevPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(prevPage => prevPage - 1);
    };

    const toggleSnackbar = () => setShow(!show);

    const getStatusLabel = (value) => {
        if (typeof value === 'string') {
            return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
        const statusObj = statuses.find(status => status.value === String(value));
        return statusObj ? statusObj.label : "Unknown";
    };

    const handleBranchChange = (selectedBranch) => {
        setCurrentPage(1);
        setSelectedBranch(selectedBranch);
        setSelectedShift(null);
        setUserId([]);

        if (selectedBranch) {
            fetchShiftsForBranch(selectedBranch.value);
        } else {
            setShifts([]);
            setUsers([]);
        }
    };

    const handleShiftChange = (selectedShift) => {
        setCurrentPage(1);
        setSelectedShift(selectedShift);

        if (selectedShift && selectedBranch) {
            fetchUsersByBranchAndShift(selectedBranch.value, selectedShift.value);
        } else {
            setUsers([]);
        }
    };

    const handleUsersChange = (selectedUsers) => {
        setCurrentPage(1);
        const values = selectedUsers ? selectedUsers.map(option => option.value) : [];
        setUserId(values);
    };

    const handleSurveyChange = (filterSurvey) => {
        setCurrentPage(1);
        const value = filterSurvey ? filterSurvey.map(option => option.value) : [];
        setFilterSurvey(value);
    };

    const handleSurveyInputChange = (inputValue) => {
        if (inputValue.length > 5) {
            fetchSurveys(['client_survey_id', 'client_project_id', 'name'], inputValue);
        }
    };

    const handleSurveyNameChange = (client_survey_id) => {
        setCurrentPage(1);
        const value = client_survey_id ? client_survey_id.map(option => option.value) : [];
        setClient_survey_id(value);
    };

    const handleSurveyNameInputChange = (inputValue) => {
        if (inputValue.length > 5) {
            fetchSurveys(['client_survey_id', 'client_project_id', 'name'], inputValue);
        }
    };

    const handleQsidChange = (qsid) => {
        setCurrentPage(1);
        const value = qsid ? qsid.map(option => option.value) : [];
        setQsid(value);
    };

    const handleQsidInputChange = (qsidNameChange) => {
        if (qsidNameChange.length >= 5) {
            setCurrentPage(1);
            setQsidNameChange(qsidNameChange);
        } else if (qsidNameChange.length === 0) {
            setQsidNameChange("");
        }
    };

    const handleStatusChange = (selectedStatus) => {
        setCurrentPage(1);
        const value = selectedStatus ? selectedStatus.map(option => option.value) : [];
        setStatus(value);
    };

    const handleCountryChange = (selectedCountryCode) => {
        setCurrentPage(1);
        const values = selectedCountryCode ? selectedCountryCode.map(option => option.value) : [];
        setSelectedCountryCode(values);
    };

    const handleClientChange = (selectedClient) => {
        setCurrentPage(1);
        const selectedClientId = selectedClient ? selectedClient.value : null;
        setSelectedClient(selectedClientId);
        if (selectedClientId) {
            fetchClientSubAccounts(selectedClientId);
        } else {
            setClientSubAccounts([]);
        }
    };

    const handleClientSubAccountsChange = (selectedClientSubAccounts) => {
        setCurrentPage(1);
        const values = selectedClientSubAccounts ? selectedClientSubAccounts.map(option => option.value) : [];
        setSelectedClientSubAccounts(values);
    };

    /* ---------- API FUNCTIONS ---------- */
    const fetchBranches = async () => {
        try {
            const response = await fetch(`${API_URL}/branches`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();
            if (data.success) {
                const branchOptions = data.data.map((branch) => ({
                    value: branch.id,
                    label: branch.name,
                }));
                setBranches(branchOptions);
            } else {
                console.error("Failed to fetch branches:", data.message);
            }
        } catch (error) {
            console.error("Error fetching branches:", error);
        }
    };

    const fetchShiftsForBranch = async (branchId) => {
        try {
            const response = await fetch(`${API_URL}/branches/${branchId}/shifts`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();
            if (data.success) {
                const shiftOptions = data.shifts.map((shift) => ({
                    value: shift.id,
                    label: shift.label || shift.type,
                }));
                setShifts(shiftOptions);
            } else {
                console.error("Failed to fetch shifts:", data.message);
                setShifts([]);
            }
        } catch (error) {
            console.error("Error fetching shifts:", error);
            setShifts([]);
        }
    };

    const fetchUsersByBranchAndShift = async (branchId, shiftId) => {
        try {
            const response = await fetch(`${API_URL}/branches/${branchId}/shifts/${shiftId}/users`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();
            if (data.branch_shift_users) {
                const userOptions = data.branch_shift_users.map((user) => ({
                    value: user.id,
                    label: user.name,
                }));
                setUsers(userOptions);
            } else {
                console.error("Failed to fetch users:", data.message);
                setUsers([]);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setUsers([]);
        }
    };

    const fetchSessions = async () => {
        setLoading(true);
        try {
            let queryString = `per_page=20&page=${currentPage}&q=${searchQuery}`;

            if (qsidNameChange) {
                queryString += `&q=${qsidNameChange}`;
            }

            if (id && id !== "null" && id !== "undefined" && isNaN(id) === false) {
                queryString += `&survey_id=${id}`;
            }

            if (userId.length > 0) {
                userId.forEach(id => {
                    queryString += `&user_id[]=${id}`;
                });
            }

            if (selectedCountryCode.length > 0) {
                selectedCountryCode.forEach(country => {
                    queryString += `&location[]=${country}`;
                });
            }

            if (client_survey_id.length > 0) {
                client_survey_id.forEach(csi => {
                    queryString += `&survey_id[]=${csi}`;
                });
            }

            if (client_project_id.length > 0) {
                client_project_id.forEach(cpi => {
                    queryString += `&survey_id[]=${cpi}`;
                });
            }

            if (survey_name.length > 0) {
                survey_name.forEach(sn => {
                    queryString += `&survey_id[]=${sn}`;
                });
            }

            if (filterSurvey.length > 0) {
                filterSurvey.forEach(sn => {
                    queryString += `&survey_id[]=${sn}`;
                });
            }

            if (qsid.length > 0) {
                qsid.forEach(sn => {
                    queryString += `&qsid[]=${sn}`;
                });
            }

            if (selectedClient) {
                queryString += `&client_id=${selectedClient}`;
            }

            if (selectedClientSubAccounts.length > 0) {
                selectedClientSubAccounts.forEach(subAccount => {
                    queryString += `&client_sub_account[]=${subAccount}`;
                });
            }

            if (fromDate) queryString += `&start_date=${fromDate}`;
            if (toDate) queryString += `&end_date=${toDate}`;

            if (status.length > 0) {
                status.forEach(s => {
                    queryString += `&status[]=${s}`;
                });
            }

            if (selectedBranch) {
                queryString += `&branch_id=${selectedBranch.value}`;
            }

            if (selectedShift) {
                queryString += `&shift_id=${selectedShift.value}`;
            }

            const response = await fetch(`${API_URL}/mc/sessions-sync?${queryString}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                }
            });

            const data = await response.json();

            if (data.success !== 1) {
                throw new Error(`${data.message}`);
            }

            // Build qsidArray from respondent_id (for Respondent ID filter)
            const qsidArray = data.sessions.data.map((session) => ({
                value: session.respondent_id,
                label: session.respondent_id
            }));

            // For survey filters, we keep empty arrays since survey data is not present
            setSurveys([]);
            setSurvey_names([]);
            setQsidArray(qsidArray);
            setSessions(data.sessions.data);
            setTotalPages(data.sessions.last_page);
            setFromData(data.sessions.from);
            setToData(data.sessions.to);
            setTotalSessions(data.sessions.total);
            setTotalCompletes(data.totalCompletes);
            setTotalRevenue(data.totalRevenue);
        } catch (err) {
            setMessage(err.message);
            setShow(true);
        } finally {
            setLoading(false);
            setPageLoading(false);
        }
    };

    const fetchSurveys = async (selectedFields = ['id', 'name'], inputValue = null) => {
        try {
            let queryParams = new URLSearchParams();
            queryParams.append("per_page", 10);
            queryParams.append("fields", selectedFields.join(','));

            if (inputValue) queryParams.append("q", inputValue);

            const response = await fetch(`${API_URL}/surveys?${queryParams.toString()}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const data = await response.json();

            if (data.success === 1) {
                const surveys = data.surveys.data.map((survey) => ({
                    value: survey.id,
                    label: survey.client_project_id ?? survey.client_survey_id,
                }));
                const surveys_names = data.surveys.data.map((survey) => ({
                    value: survey.id,
                    label: survey.name
                }));
                setSurveys(surveys);
                setSurvey_names(surveys_names);
            } else {
                console.error("Failed to fetch surveys:", data.message);
            }
        } catch (error) {
            console.error("Error fetching surveys:", error);
        }
    };

    const fetchClients = async () => {
        try {
            const response = await fetch(`${API_URL}/clients?per_page=250`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();
            if (data.success === 1) {
                const clientOptions = data.clients.data.map((client) => ({
                    value: client.id,
                    label: client.name,
                }));
                setClients(clientOptions);
            } else {
                console.error("Failed to fetch clients:", data.message);
            }
        } catch (error) {
            console.error("Error fetching clients:", error);
        }
    };

    const fetchClientSubAccounts = async (id) => {
        try {
            const response = await fetch(`${API_URL}/surveys/subaccounts?client_id=${id}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();
            if (data.success === 1) {
                const subAccountsOptions = data.subAccounts.map((val) => ({
                    value: val,
                    label: val,
                }));
                setClientSubAccounts(subAccountsOptions);
            } else {
                console.error("Failed to fetch client subaccounts:", data.message);
            }
        } catch (error) {
            console.error("Error fetching client subaccounts:", error);
        }
    };

    const exportData = async () => {
        setExportLoading(true);
        try {
            let queryParams = [];

            if (selectedClient) queryParams.push(`client_id=${selectedClient}`);

            if (Array.isArray(userId) && userId.length > 0) {
                userId.forEach(user => {
                    queryParams.push(`user_id[]=${user}`);
                });
            }

            if (selectedCountryCode.length > 0) {
                selectedCountryCode.forEach(country => {
                    queryParams.push(`country_code[]=${country}`);
                });
            }

            if (selectedClientSubAccounts.length > 0) {
                selectedClientSubAccounts.forEach(subAccount => {
                    queryParams.push(`client_sub_account[]=${subAccount}`);
                });
            }

            if (status.length > 0) {
                status.forEach(s => {
                    queryParams.push(`status[]=${s}`);
                });
            }

            if (client_survey_id.length > 0) {
                client_survey_id.forEach(csi => {
                    queryParams.push(`survey_id[]=${csi}`);
                });
            }

            if (client_project_id.length > 0) {
                client_project_id.forEach(cpi => {
                    queryParams.push(`survey_id[]=${cpi}`);
                });
            }

            if (survey_name.length > 0) {
                survey_name.forEach(sn => {
                    queryParams.push(`survey_id[]=${sn}`);
                });
            }

            if (filterSurvey.length > 0) {
                filterSurvey.forEach(sn => {
                    queryParams.push(`survey_id[]=${sn}`);
                });
            }

            if (qsid.length > 0) {
                qsid.forEach(sn => {
                    queryParams.push(`qsid[]=${sn}`);
                });
            }

            if (fromDate) queryParams.push(`start_date=${fromDate}`);
            if (toDate) queryParams.push(`end_date=${toDate}`);
            if (qsidNameChange) queryParams.push(`q=${qsidNameChange}`);
            if (searchQuery) queryParams.push(`q=${searchQuery}`);

            if (selectedBranch) queryParams.push(`branch_id=${selectedBranch.value}`);
            if (selectedShift) queryParams.push(`shift_id=${selectedShift.value}`);

            const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

            const response = await fetch(`${API_URL}/mc/sessions-sync/export${queryString}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            const now = new Date();
            const formattedDate = now.getFullYear() + '-' +
                String(now.getMonth() + 1).padStart(2, '0') + '-' +
                String(now.getDate()).padStart(2, '0') + '_' +
                String(now.getHours()).padStart(2, '0') + '-' +
                String(now.getMinutes()).padStart(2, '0') + '-' +
                String(now.getSeconds()).padStart(2, '0');

            const filename = `quest_sessions_${formattedDate}.csv`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setMessage(err.message);
            setShow(true);
        } finally {
            setExportLoading(false);
        }
    };

    const handleDownload = () => {
        exportData();
    };

    /* ---------- EFFECT HOOKS ---------- */
    useEffect(() => {
        fetchSessions();
    }, [currentPage, searchQuery, userId, selectedCountryCode, status, fromDate, toDate,
        client_survey_id, client_project_id, survey_name, filterSurvey, qsid,
        selectedClient, selectedClientSubAccounts, qsidNameChange, selectedBranch, selectedShift]);

    useEffect(() => {
        fetchBranches();
        fetchClients();
    }, []);

    /* ---------- RENDER SECTION ---------- */
    if (pageLoading || !isAuthReady) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh">
                    {/* <CircularProgress size={70} />
                    <SoftTypography variant="h6" ml={2}>
                        Loading page, please wait...
                    </SoftTypography> */}
                    <FlagLoader />
                </SoftBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftSnackbar
                color="primary"
                icon="notifications"
                title="Soft UI Dashboard"
                content={message ?? ''}
                open={show}
                close={toggleSnackbar}
            />

            <div style={{ display: "flex", gap: "10px" }}>
                {/* Main Content Card */}
                <Card style={{ flex: 1, borderRadius: "0px", padding: "10px" }}>

                    {/* Branch and Shift Filters */}
                    {/* <div style={{ display: "flex", gap: "5px", marginBottom: "20px", flexWrap: "wrap" }}> */}
                    {/* {userCanSearch("branch") && (
                            <div style={{ flex: 1 }}>
                                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                                    Branch
                                </Typography>
                                <SearchableSelect
                                    className="basic-select"
                                    classNamePrefix="select"
                                    name="branch"
                                    options={branches}
                                    isMulti={false}
                                    fontSize={"10px"}
                                    onChange={handleBranchChange}
                                    value={selectedBranch}
                                />
                            </div>
                        )} */}

                    { /*{ {userCanSearch("shift") && (
                            <div style={{ flex: 1 }}>
                                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                                    Shift
                                </Typography>
                                <SearchableSelect
                                    className="basic-select"
                                    classNamePrefix="select"
                                    name="shift"
                                    options={shifts}
                                    isMulti={false}
                                    fontSize={"10px"}
                                    onChange={handleShiftChange}
                                    value={selectedShift}
                                    isDisabled={!selectedBranch}
                                />
                            </div>
                        )} */ }
                    {/* </div> */}

                    {/* Search Filters Row */}
                    {/* <div style={{ display: "flex", gap: "5px", marginBottom: "10px", flexWrap: "wrap" }}> */}
                    { /* {userCanSearch("users") && (
                            <div style={{ flex: 1 }}>
                                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                                    Users
                                </Typography>
                                <SearchableSelect
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    name="user_id"
                                    options={users}
                                    isMulti={true}
                                    fontSize={"10px"}
                                    onChange={handleUsersChange}
                                    isDisabled={!selectedShift}
                                />
                            </div>
                        )} */}

                    { /*   {userCanSearch("client_survey_id") && (
                            <div style={{ flex: 1 }}>
                                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                                    Client Survey ID
                                </Typography>
                                <SearchableSelect
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    name="survey_id"
                                    options={surveys}
                                    isMulti={true}
                                    fontSize={"10px"}
                                    onChange={handleSurveyChange}
                                    onInputChange={handleSurveyInputChange}
                                />
                            </div>
                        )} */ }

                    {/*}   {userCanSearch("quest_id") && (
                            <div style={{ flex: 1 }}>
                                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                                    Project ID
                                </Typography>
                                <SearchableSelect
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    name="quest_id"
                                    options={survey_names}
                                    isMulti={true}
                                    fontSize={"10px"}
                                    onChange={handleSurveyNameChange}
                                    onBlur={handleSurveyNameInputChange}
                                />
                            </div>
                        )} */}

                    {  /* {userCanSearch("countries") && (
                            <div style={{ flex: 1 }}>
                                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                                    Country
                                </Typography>
                                <SearchableSelect
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    name="country"
                                    options={countryCodes}
                                    isMulti={true}
                                    fontSize={"12px"}
                                    onChange={handleCountryChange}
                                />
                            </div>
                        )} */ }


                    {/* </div> */}

                    {/* Sessions Table */}
                    <div style={{ overflowX: "auto" }}>
                        {loading ? (
                            // <SoftBox display="flex" justifyContent="center" alignItems="center" height="300px">
                            //     <CircularProgress size={50} />
                            // </SoftBox>
                            <FlagLoader />
                        ) : (
                            <>
                                {/* Pagination Controls */}
                                <SoftBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    p={1}
                                    style={{ width: "100%", fontSize: "13px" }}
                                >
                                    <SoftBox fontWeight="small" style={{ width: "100%" }}>
                                        Showing {fromData} to {toData} of {totalSessions} entries
                                    </SoftBox>

                                    <SoftBox display="flex" justifyContent="end" alignItems="center" p={1} style={{ width: "100%" }}>
                                        <SoftPagination item onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                            <Icon sx={{ fontWeight: "bold" }}>first_page</Icon>
                                        </SoftPagination>

                                        <SoftPagination item onClick={handlePreviousPage} disabled={currentPage === 1}>
                                            <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                                        </SoftPagination>

                                        <span
                                            style={{
                                                margin: "0 5px",
                                                width: "120px",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <span width="5rem" mx={1} style={{ fontSize: "9px" }}>
                                                <SoftInput
                                                    inputProps={{ type: "number", min: 1, max: totalPages }}
                                                    value={currentPage}
                                                    onChange={handleInputChange}
                                                />
                                            </span>
                                            /<span>{totalPages}</span>
                                        </span>

                                        <SoftPagination item onClick={handleNextPage} disabled={currentPage === totalPages}>
                                            <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                                        </SoftPagination>

                                        <SoftPagination item onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                                            <Icon sx={{ fontWeight: "bold" }}>last_page</Icon>
                                        </SoftPagination>
                                    </SoftBox>
                                </SoftBox>

                                {/* Revenue Info (with permission check) */}
                                {userHas("revenue_info") && (
                                    <SoftBox style={{ width: "100%", fontSize: "13px" }}>
                                        <span style={{ color: "rgb(23, 193, 232)" }}>Revenue:</span> {totalRevenue} |{" "}
                                        <span style={{ color: "rgb(23, 193, 232)" }}>Completes:</span> {totalCompletes}
                                    </SoftBox>
                                )}

                                {/* Sessions Table */}
                                <Table style={{ width: "100%" }}>
                                    <TableHead style={{ padding: "0px", display: "contents" }}>
                                        <TableRow style={{ backgroundColor: "#5c91c5", color: "#fff", width: "100%" }}>
                                            {visibleColumns.map((column) => (
                                                <TableCell key={column.key} style={{ color: "#fff", fontSize: "8.5px", textAlign: "center" }}>
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sessions.map((session, rowIndex) => {
                                            return (
                                                <TableRow key={rowIndex}>
                                                    {visibleColumns.map((column) => {
                                                        let content;
                                                        switch (column.key) {
                                                            case "client_survey_id":
                                                                // Now shows bid_id
                                                                content = session.bid_id || 'N/A';
                                                                break;
                                                            case "client_quota_id":
                                                                // New column showing quota_id
                                                                content = session.quota_id || 'N/A';
                                                                break;
                                                            case "survey_name":
                                                                content = 'N/A'; // Not available
                                                                break;
                                                            case "user":
                                                                content = 'N/A'; // Not available
                                                                break;
                                                            case "client":
                                                                content = 'N/A'; // Not available
                                                                break;
                                                            case "respondent_id":
                                                                content = session.respondent_id || 'N/A';
                                                                break;
                                                            case "survey_cpi":
                                                                content = session.cpi !== undefined ? session.cpi : 'N/A';
                                                                break;
                                                            case "status":
                                                                content = session.status ? getStatusLabel(session.status) : 'N/A';
                                                                break;
                                                            case "status_detail":
                                                                // New column for status_detail
                                                                content = session.status_detail || 'N/A';
                                                                break;
                                                            case "qualified_at":
                                                                // New column for qualified_at
                                                                content = session.qualified_at ?
                                                                    new Date(session.qualified_at).toLocaleString("en-GB").replace(",", "").replace(/\//g, "-") :
                                                                    'N/A';
                                                                break;
                                                            case "start_date":
                                                                content = session.created_at ?
                                                                    new Date(session.created_at).toLocaleString("en-GB").replace(",", "").replace(/\//g, "-") :
                                                                    'N/A';
                                                                break;
                                                            case "end_date":
                                                                content = session.finished_at ?
                                                                    new Date(session.finished_at).toLocaleString("en-GB").replace(",", "").replace(/\//g, "-") :
                                                                    'N/A';
                                                                break;
                                                            default:
                                                                content = "N/A";
                                                        }
                                                        return (
                                                            <TableCell key={column.key} style={{ fontSize: "9px", textAlign: "center" }}>
                                                                {content}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </>
                        )}
                    </div>
                </Card>

                {/* Sidebar Card */}
                <div style={{ display: "flex", flexDirection: "column", gap: "50px" }}>
                    <Card style={{ padding: "5px", width: "200px", height: "100%", borderRadius: "0px" }}>
                        {/* Export Button with Permission Check */}
                        <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography variant="p" style={{ fontSize: "12px" }}>Count of SessionId</Typography>
                                {userCanExport() && (
                                    <IconButton
                                        color="primary"
                                        onClick={handleDownload}
                                        disabled={exportLoading}
                                    >
                                        {exportLoading ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <DownloadIcon />
                                        )}
                                    </IconButton>
                                )}
                            </div>
                            <Typography variant="h4" color="primary" style={{ fontSize: "20px", textAlign: "center" }}>
                                {totalSessions}
                            </Typography>
                        </div>

                        {/* Date Range Filters with Permission Check */}
                        {userCanSearch("date_range") && (
                            <>
                                <div>
                                    <Typography variant="p" style={{ fontSize: "12px" }}>Start Date</Typography>
                                    <SoftInput type="datetime-local" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                                </div>
                                <div>
                                    <Typography variant="p" style={{ fontSize: "12px" }}>End Date</Typography>
                                    <SoftInput type="datetime-local" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                                </div>
                            </>
                        )}

                        {/* General Search with Permission Check */}
                        {userCanSearch("general_search") && (
                            <div>
                                <Typography variant="p" style={{ fontSize: "12px" }}>Type here</Typography>
                                <SoftInput type="text" value={searchQuery} placeholder="Type here" onChange={(e) => setSearchQuery(e.target.value)} />
                            </div>
                        )}

                        {/* Respondent ID Filter with Permission Check */}
                        {userCanSearch("respondent_id") && (
                            <div>
                                <Typography variant="p" style={{ fontSize: "12px" }}>Respondent ID</Typography>
                                <SearchableSelect
                                    className="basic-select"
                                    classNamePrefix="select"
                                    name="respondent_id"
                                    options={qsidArray}
                                    isMulti={true}
                                    fontSize={"10px"}
                                    onChange={handleQsidChange}
                                    onInputChange={handleQsidInputChange}
                                />
                            </div>
                        )}

                        {userCanSearch("status") && (
                            <div style={{ marginTop: "12px" }}>
                                <Typography variant="p" style={{ fontSize: "12px" }}>
                                    Status
                                </Typography>
                                <SearchableSelect
                                    className="basic-select"
                                    classNamePrefix="select"
                                    name="status"
                                    options={statuses}
                                    isMulti={true}
                                    fontSize={"10px"}
                                    onChange={handleStatusChange}
                                />
                            </div>
                        )}

                        {/* Client Filters with Permission Check */}
                        {/* {userCanSearch("clients") && (
                            <>
                                <div style={{ marginTop: "12px" }}>
                                    <Typography variant="p" style={{ fontSize: "12px" }}>Clients</Typography>
                                    <SearchableSelect
                                        className="basic-select"
                                        classNamePrefix="select"
                                        name="client"
                                        options={clients}
                                        isMulti={false}
                                        fontSize={"10px"}
                                        onChange={handleClientChange}
                                    />
                                </div>
                                <div style={{ marginTop: "12px" }}>
                                    <Typography variant="p" style={{ fontSize: "12px" }}>Clients Accounts Name</Typography>
                                    <SearchableSelect
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                        name="subClients"
                                        options={clientSubAccounts}
                                        isMulti={true}
                                        fontSize={"10px"}
                                        onChange={handleClientSubAccountsChange}
                                    />
                                </div>
                            </>
                        )} */}
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default MorningConsultSessionsList;
